/**
 * Authenticated app shell — the Claude Design top bar mapped onto the real
 * dashboard data and routing.
 *
 * Layout (54px bar): brand (pulsing live-dot + "HOTLINE" wordmark) · role-aware
 * primary nav (Tickets / Widgets / Clusters, plus Organizations for meta-admins,
 * each with the design icon) · spacer · tools (Search/⌘K trigger, the meta-admin
 * org-scope pill, a connection indicator, the theme toggle, and the user-menu
 * avatar). The routed page renders in `<main>` via React Router's `<Outlet>`.
 *
 * Behavior is unchanged from the previous shell: the same nav routes, the same
 * meta-vs-org role gating, the real auth identity/sign-out, the real org scope,
 * and the existing ThemeProvider/ThemeToggle. The design's mock data is mapped
 * onto that — fields the real data lacks (e.g. nav counts) are omitted.
 *
 * Built on the design-system primitives (`components/ui`) + semantic theme
 * tokens, so dark and light both render correctly; no hardcoded colors.
 */
import { useEffect, useRef, type ReactElement } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import type { Organization, Role } from '@hotline/contracts';

import { useAuth } from '../auth/AuthContext.js';
import { useAsync } from '../lib/useAsync.js';
import { useOrgScope } from '../org/OrgScopeContext.js';
import { ThemeToggle } from '../theme/ThemeToggle.js';
import { Brand } from './Brand.js';
import { OrgScopePill } from './OrgScopePill.js';
import { Icon, type IconName } from './ui/icons.js';
import {
  Avatar,
  Conn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from './ui/index.js';

interface NavEntry {
  to: string;
  label: string;
  icon: IconName;
  /** When true, only meta-admins see this entry. */
  meta?: boolean;
}

/** Primary nav — order/labels match the design (`Tickets · Widgets · Clusters`,
 * with `Organizations` appended for meta-admins). Routes are the real ones. */
const NAV: readonly NavEntry[] = [
  { to: '/org/tickets', label: 'Tickets', icon: 'inbox' },
  { to: '/org/instances', label: 'Widgets', icon: 'widgets' },
  { to: '/org/clusters', label: 'Clusters', icon: 'clusters' },
  { to: '/meta/orgs', label: 'Organizations', icon: 'org', meta: true },
];

const navItemClasses =
  'flex items-center gap-[7px] rounded-md px-3 py-[7px] text-[13.5px] font-semibold ' +
  'text-muted transition-colors [&_svg]:opacity-85 ' +
  'hover:bg-surface-hover hover:text-text ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
  'aria-[current=page]:bg-surface-2 aria-[current=page]:text-text';

/**
 * What each role is called in the account menu. One entry per role in the
 * hierarchy — this used to be `isMetaAdmin ? 'Meta-admin' : 'Operator'`, which
 * silently labelled a project admin and a reporter as operators.
 */
const ROLE_LABELS: Record<Role, string> = {
  META_ADMIN: 'Platform admin',
  ORG_ADMIN: 'Organization admin',
  PROJECT_ADMIN: 'Project admin',
  REPORTER: 'Reporter',
};

export function Layout(): ReactElement {
  const { client, user, principal, isMetaAdmin, logout } = useAuth();
  const { orgId, isMeta, setOrgId } = useOrgScope();
  const navigate = useNavigate();

  // The scope pill names its organization and offers the others, so it needs the
  // same list the Organizations screen shows. Meta-admin only — nobody else has
  // a choice of scope to make.
  const orgs = useAsync<Organization[]>(
    () => (isMeta ? client.api.listOrganizations() : Promise.resolve([])),
    [client, isMeta],
  );
  const searchRef = useRef<HTMLButtonElement>(null);

  // ⌘K / Ctrl-K focuses the search entry point (the bar's search affordance).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const signOut = (): void => {
    logout();
    navigate('/login', { replace: true });
  };

  // Name first, then email. NEVER the user id: a cuid in the account menu is
  // not an identity, it is a symptom — it means the session was restored
  // without a user, which is exactly what a stale token used to do.
  const identity = user?.name?.trim() || user?.email || '';
  const initials = (identity.trim()[0] ?? '?').toUpperCase();
  const roleLabel = principal ? ROLE_LABELS[principal.role] : '';

  return (
    <div className="flex min-h-screen flex-col bg-bg text-text">
      <header
        className={cn(
          'sticky top-0 z-40 flex h-[54px] flex-shrink-0 items-center gap-[18px] px-[18px]',
          'border-b border-border bg-surface/70 backdrop-blur-[14px] backdrop-saturate-150',
        )}
      >
        <Brand to="/org/tickets" />

        <nav className="flex items-center gap-0.5" aria-label="Primary">
          {NAV.filter((n) => !n.meta || isMetaAdmin).map((n) => {
            const IconCmp = Icon[n.icon];
            return (
              <NavLink key={n.to} to={n.to} className={navItemClasses}>
                <IconCmp size={16} />
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <span className="flex-1" />

        <div className="flex items-center gap-[9px]">
          <button
            ref={searchRef}
            type="button"
            aria-label="Search"
            className={cn(
              'flex h-8 min-w-[188px] items-center gap-[9px] rounded-md pl-[11px] pr-2.5',
              'border border-border bg-surface-2 text-[12.5px] text-faint transition-colors',
              'hover:border-border-strong hover:text-muted',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            )}
          >
            <Icon.search size={15} />
            Search
            <kbd className="mono ml-auto rounded-[5px] border border-border bg-surface-3 px-[5px] py-0.5 text-[10.5px] text-faint">
              ⌘K
            </kbd>
          </button>

          {isMeta && orgId ? (
            <OrgScopePill
              orgId={orgId}
              organizations={orgs.data ?? []}
              onSelect={setOrgId}
              onClearScope={() => setOrgId(undefined)}
            />
          ) : null}

          <Conn status="live" />

          <ThemeToggle />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="User menu"
                className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <Avatar fallback={initials} size="sm" color="#3b6ef0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[210px]">
              <div className="flex items-center gap-2.5 px-[9px] pb-2.5 pt-2">
                <Avatar fallback={initials} color="#3b6ef0" />
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-bold text-text">
                    {identity || 'Account'}
                  </div>
                  <div className="text-[11.5px] text-faint">{roleLabel}</div>
                </div>
              </div>
              <DropdownMenuSeparator />
              {/* Profile / Preferences screens don't exist yet — shown for parity
                  with the design but disabled so they don't route nowhere. */}
              <DropdownMenuItem disabled>
                <Icon.user size={15} />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <Icon.sliders size={15} />
                Preferences
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={signOut}
                className="text-danger [&_svg]:text-danger data-[highlighted]:bg-danger-soft"
              >
                <Icon.lock size={15} />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1200px] flex-1 px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}
